import { EXISTING_LAYER_ID, IkeToken, type VariantLayerNode } from '@ikegps/core-api';
import { action, makeObservable, observable } from 'mobx';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { IMithrilComponent } from '../../components/mithril.component';
import { addStyles, DefaultTheme, rgba } from '../../components/theme';
import { ColorPaletteOrange } from '../../components/theme.palette';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { type FlatVariantLayer, VariantLayerState } from '../../state/variant.layer.state';

const CSS = {
	container: 'c-VariantLayerSelector',
	header: 'c-VariantLayerSelector__Header',
	headerDisabled: 'c-VariantLayerSelector__Header--Disabled',
	count: 'c-VariantLayerSelector__Count',
	chevron: 'c-VariantLayerSelector__Chevron',
	chevronDisabled: 'c-VariantLayerSelector__Chevron--Disabled',
	list: 'c-VariantLayerSelector__List',
	row: 'c-VariantLayerSelector__Row',
	rowSelected: 'c-VariantLayerSelector__Row--Selected',
	glyph: 'c-VariantLayerSelector__Glyph',
	label: 'c-VariantLayerSelector__Label',
};

// Each nesting level indents by one branch-arrow width so children line up under their parent.
const INDENT_PX = 20;

addStyles({
	[CSS.container]: {
		// Small gap between the selector and the collection rows that follow it.
		'margin-bottom': '5px',
	},
	[CSS.header]: {
		display: 'flex',
		'align-items': 'center',
		gap: DefaultTheme.space(1),
		padding: `${DefaultTheme.space(2)} ${DefaultTheme.space(2)}`,
		cursor: 'pointer',
		'font-weight': DefaultTheme.font.weight.bold,
		color: DefaultTheme.font.color.primary,
		'border-bottom': `1px solid ${DefaultTheme.font.color.divider}`,
		'user-select': 'none',
	},
	[CSS.headerDisabled]: {
		color: DefaultTheme.font.color.disabled,
		cursor: 'default',
	},
	[CSS.count]: {
		'margin-left': 'auto',
		color: DefaultTheme.font.color.supporting,
		'font-weight': DefaultTheme.font.weight.normal,
	},
	[CSS.chevron]: {
		color: DefaultTheme.font.color.supporting,
		'font-size': '20px',
	},
	[CSS.chevronDisabled]: {
		// No count is shown when disabled, so the chevron itself pushes to the right edge.
		'margin-left': 'auto',
		color: DefaultTheme.font.color.disabled,
	},
	[CSS.list]: {
		'border-bottom': `1px solid ${DefaultTheme.font.color.divider}`,
	},
	[CSS.row]: {
		display: 'flex',
		'align-items': 'center',
		gap: DefaultTheme.space(1),
		padding: `${DefaultTheme.space(2)} ${DefaultTheme.space(2)}`,
		cursor: 'pointer',
		color: DefaultTheme.font.color.primary,
		'&:hover': {
			'background-color': DefaultTheme.background.lightGrey,
		},
	},
	[CSS.rowSelected]: {
		'background-color': rgba(ColorPaletteOrange.C50),
		'&:hover': {
			'background-color': rgba(ColorPaletteOrange.C50),
		},
	},
	[CSS.glyph]: {
		color: DefaultTheme.font.color.supporting,
		'font-size': '18px',
		'flex-shrink': 0,
	},
	[CSS.label]: {
		overflow: 'hidden',
		'text-overflow': 'ellipsis',
		'white-space': 'nowrap',
	},
});

function layerLabel(node: VariantLayerNode): string {
	if (node.id === EXISTING_LAYER_ID) {
		return Translate(T.VARIANT_LAYER_EXISTING);
	}
	return node.name.length > 0 ? node.name : node.id;
}

/**
 * Job-level selector for the variant layer to view. A reusable "Variants" menu that opens to the
 * job's layer tree — the Existing (field-collected) layer plus each read-only variant, branched and
 * lock-marked. Selecting a layer updates {@link VariantLayerState}; the list and map read that
 * selection to filter. Gated by the master {@link IkeToken.FEATURE_VERIFY_LAYERS} token: with the
 * feature off it renders nothing anywhere.
 *
 * Placement per surface: Map Mode and PhotoMeasure mount it below the toolbar; /map mounts it as the
 * first child inside an open job, and only when the big-map token is on. It renders nothing when the
 * job has no variants, unless `showWhenEmpty` asks for a disabled (greyed) placeholder header.
 */
export interface VariantLayerSelectorOptions {
	/** Render a disabled placeholder when there is no selected job / no variants, instead of nothing. */
	showWhenEmpty?: boolean;
}

export class VariantLayerSelector extends IMithrilComponent {
	@observable private expanded = false;

	constructor(
		private tagId: () => string | null | undefined,
		private options: VariantLayerSelectorOptions = {}
	) {
		super();
		makeObservable(this);
	}

	@action private toggle = (): void => {
		this.expanded = !this.expanded;
	};

	private choose = (tagId: string | null | undefined, layerId: string): void => {
		// The panel is a toggle, not a dropdown: picking a layer does NOT close it. It stays open until
		// the user collapses it from the header, so they can switch layers without re-opening each time.
		if (tagId != null) {
			VariantLayerState.select(tagId, layerId);
		}
	};

	get nodes(): MithrilVNode {
		// Master switch: with the feature off there is no layer selector on any page.
		if (!Services.Token.has(IkeToken.FEATURE_VERIFY_LAYERS)) {
			return null;
		}

		const tagId = this.tagId();
		const enabled = VariantLayerState.hasLayers(tagId);
		if (!enabled && !this.options.showWhenEmpty) {
			return null;
		}

		const layers = enabled ? VariantLayerState.flatLayers(tagId) : [];
		const open = enabled && this.expanded;

		return m('div', { className: CSS.container }, [
			this.header(enabled, layers.length, open),
			open ? this.list(tagId, layers) : null,
		]);
	}

	private header(enabled: boolean, count: number, open: boolean): MithrilVNode {
		return m(
			'div',
			{
				className: `${CSS.header} ${enabled ? '' : CSS.headerDisabled}`,
				onclick: enabled ? this.toggle : undefined,
				title: enabled ? Translate(T.VARIANT_LAYER_TITLE) : '',
				// Always an explicit value: mithril leaves a stale `aria-disabled="true"` in the DOM if this
				// flips to undefined, so the header would read disabled after it becomes enabled.
				'aria-disabled': enabled ? 'false' : 'true',
				'data-testid': 'variant-layer-header',
			},
			[
				m('span', {}, Translate(T.VARIANT_LAYER_HEADER)),
				enabled ? m('span', { className: CSS.count, 'data-testid': 'variant-layer-count' }, String(count)) : null,
				Icon.view(open ? MaterialIcon.KEY_DOWN : MaterialIcon.KEY_LEFT, {
					className: `${CSS.chevron} ${enabled ? '' : CSS.chevronDisabled}`,
				}),
			]
		);
	}

	private list(tagId: string | null | undefined, layers: FlatVariantLayer[]): MithrilVNode {
		const selectedId = VariantLayerState.selectedLayerId(tagId);
		return m(
			'div',
			{ className: CSS.list },
			layers.map(({ node, depth }) => this.row(tagId, node, depth, node.id === selectedId))
		);
	}

	private row(
		tagId: string | null | undefined,
		node: VariantLayerNode,
		depth: number,
		selected: boolean
	): MithrilVNode {
		const isVariant = node.id !== EXISTING_LAYER_ID;
		return m(
			'div',
			{
				className: `${CSS.row} ${selected ? CSS.rowSelected : ''}`,
				style: `padding-left:${DefaultTheme.spacingFactor * 2 + depth * INDENT_PX}px`,
				'data-testid': `variant-layer-${node.id}`,
				onclick: () => this.choose(tagId, node.id),
			},
			[
				depth > 0 ? Icon.view(MaterialIcon.SUBDIRECTORY_ARROW_RIGHT, { className: CSS.glyph }) : null,
				isVariant
					? Icon.view(MaterialIcon.LOCK, { className: CSS.glyph, title: Translate(T.VARIANT_READ_ONLY) })
					: null,
				m('span', { className: CSS.label }, layerLabel(node)),
			]
		);
	}
}
